import { createElement, getElement, getElements } from '@by-association-only/theme-utils';
export class RichText {
  static start() {
    return new this();
  }

  constructor() {
    this.wrapIframes();
    this.wrapImages();
    this.wrapTables();
    this.tableHoverEffect();
  }
  /**
   * This wraps all of the tables inside rich text areas so we can perform responsive styles easier
   *
   * @param {Array} elements - The tables we want to wrap
   * @param {String} [wrapClass=rte-RichText_Table] - The class name we want to use on the wrapping
   *   elements
   */


  wrapTables({
    elements = getElements('.rte-RichText table'),
    wrapClass = 'rte-RichText_Table'
  } = {}) {
    elements.forEach(el => {
      const allInnerElements = getElements('*', {
        context: el
      });
      allInnerElements.forEach(el => removeStyle(el));
      el.removeAttribute('width');
      el.removeAttribute('height');
      const div = createElement('div', {
        class: wrapClass
      });
      div.innerHTML = el.outerHTML;
      el.parentNode.insertBefore(div, el);
      el.remove();

      function removeStyle(el) {
        el.removeAttribute('style');
      }
    });
  }
  /**
   * This wraps all of the iframes inside rich text areas so we can perform responsive styles easier
   *
   * @param {Array} elements - The iframes we want to wrap
   * @param {String} [wrapClass=rte-RichText_Iframe] - The class name we want to use on the wrapping
   *   elements
   */


  wrapIframes({
    elements = getElements('.rte-RichText iframe'),
    wrapClass = 'rte-RichText_Iframe'
  } = {}) {
    elements.forEach(el => {
      const div = createElement('div', {
        class: wrapClass
      });
      div.innerHTML = el.outerHTML;
      el.parentNode.insertBefore(div, el);
      el.remove(); // Re-set the src attribute on each iframe after page load
      // for Chrome's "incorrect iFrame content on 'back'" bug.
      // https://code.google.com/p/chromium/issues/detail?id=395791
      // Need to specifically target video and admin bar

      const newIframe = getElement('iframe', div);
      newIframe.setAttribute('src', newIframe.getAttribute('src'));
    });
  }
  /**
   * This wraps all of the images inside rich text areas so we can perform responsive styles easier
   *
   * @param {Array} elements - The images we want to wrap
   * @param {String} [wrapClass=rte-RichText_Image] - The class name we want to use on the wrapping
   *   elements
   */


  wrapImages({
    elements = getElements('.rte-RichText img'),
    wrapClass = 'rte-RichText_Image'
  } = {}) {
    const fullWidthAlt = '[full-width]';
    elements.forEach(image => {
      const alt = image.alt || '';
      const shouldBeFullWidth = alt.startsWith(fullWidthAlt);
      const isJustFullWidthAlt = alt !== '' ? alt.trim() === fullWidthAlt : false;

      if (alt !== '') {
        image.alt = image.alt.replace(fullWidthAlt, '');
      }

      if (shouldBeFullWidth) {
        wrapClass = `${wrapClass} ${wrapClass}-full`;
      }

      const div = createElement('div', {
        class: wrapClass
      });
      div.innerHTML = image.outerHTML;

      if (alt !== '' && !isJustFullWidthAlt) {
        const caption = createElement('div', {
          class: 'rte-RichText_ImageCaption'
        });
        caption.textContent = alt;
        div.innerHTML += caption.outerHTML;
      }

      const rootElement = image.closest('p');
      rootElement.parentNode.replaceChild(div, rootElement);
    });
  }

  tableHoverEffect(className = '.rte-RichText_Table') {
    const classes = {
      active: 'rte-RichText_TableActive',
      inBetween: 'rte-RichText_TableInBetween'
    };
    const tables = getElements(`${className} table`);
    tables.forEach(table => {
      const trs = getElements('tr', {
        context: table
      });
      const tds = getElements('td', {
        context: table
      });
      table.addEventListener('mouseover', onMouseOver);
      table.addEventListener('mouseleave', onMouseLeave);

      function onMouseLeave() {
        resetTds();
      }

      function onMouseOver(e) {
        resetTds();
        const td = e.target.closest('td');
        if (!td) return;
        const [rowIndex, columnIndex] = [getTdRowIndex(td), getTdColumnIndex(td)];
        activateTds(rowIndex, columnIndex);
      }

      function resetTds() {
        tds.forEach(td => td.removeAttribute('class'));
      }

      function getTdRowIndex(td) {
        const tr = td.closest('tr');
        return trs.findIndex(t => t === tr);
      }

      function getTdColumnIndex(td) {
        const tr = td.closest('tr');
        const tds = getElements('td', {
          context: tr
        });
        return tds.findIndex(t => t === td);
      }

      function activateTds(maxRowIndex, maxColumnIndex) {
        trs.forEach(handleTr);

        function handleTr(tr, currentRowIndex) {
          const tds = getElements('td', {
            context: tr
          });
          tds.forEach(handleTd);

          function handleTd(td, columnIndex) {
            td.classList.add(getTdClass(columnIndex));
          }

          function getTdClass(currentColumnIndex) {
            if (currentRowIndex === maxRowIndex) {
              if (currentColumnIndex === 0) {
                return classes.active;
              } else if (currentColumnIndex === maxColumnIndex) {
                return classes.active;
              } else if (currentColumnIndex < maxColumnIndex) {
                return classes.inBetween;
              }
            } else if (currentRowIndex < maxRowIndex) {
              if (currentRowIndex === 0) {
                if (currentColumnIndex === maxColumnIndex) {
                  return classes.active;
                }
              } else if (currentRowIndex === maxRowIndex) {
                if (currentColumnIndex === maxColumnIndex) {
                  return classes.active;
                }
              } else if (currentRowIndex < maxRowIndex) {
                if (currentColumnIndex === maxColumnIndex) {
                  return classes.inBetween;
                }
              }
            }

            return null;
          }
        }
      }
    });
  }

}